import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import AdminLoginGate from '../components/AdminLoginGate';
import AdminTabs from '../components/AdminTabs';
import { API, authHeaders, useAdminAuth } from '../lib/adminAuth';
import { Eye, EyeOff } from 'lucide-react';

const STATUSES = [
  { key: 'new', label: 'New', cls: 'bg-[#FEF7E6] text-[#B97400]' },
  { key: 'approved', label: 'Approved', cls: 'bg-emerald-100 text-emerald-800' },
  { key: 'declined', label: 'Declined', cls: 'bg-red-100 text-red-700' },
  { key: 'info_requested', label: 'Info Requested', cls: 'bg-blue-100 text-blue-800' },
];

function StatusPill({ status }) {
  const meta = STATUSES.find((s) => s.key === status) || STATUSES[0];
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}>{meta.label}</span>;
}

function maskBankNumber(num) {
  if (!num) return '—';
  if (num.length <= 4) return num;
  return `${'•'.repeat(num.length - 4)}${num.slice(-4)}`;
}

function RequestRow({ row, onStatusChange, isOpen, onToggle }) {
  const [showBank, setShowBank] = useState(false);
  return (
    <div className="border border-zinc-200 bg-white" data-testid={`donation-request-row-${row.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 sm:px-6 py-4 flex flex-wrap items-start justify-between gap-3 text-left hover:bg-zinc-50"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <StatusPill status={row.status} />
            <span className="text-xs text-[#52525B]">
              {new Date(row.submitted_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div className="font-bold text-[#0A0A0A]">{row.organisation_name}</div>
          <div className="text-sm text-[#52525B]">{row.organisation_type} &middot; <span className="font-semibold text-[#0A0A0A]">{row.amount_requested}</span></div>
          <div className="text-xs text-[#71717A] mt-1">{row.contact_name} &middot; {row.contact_email} &middot; {row.contact_phone}</div>
        </div>
        <span className="text-xs text-[#52525B] uppercase tracking-widest font-semibold">{isOpen ? 'Hide' : 'View'}</span>
      </button>

      {isOpen && (
        <div className="px-4 sm:px-6 pb-6 pt-2 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5 text-sm border-t border-zinc-200">
          <FieldRow label="Organisation address" value={row.organisation_address} />
          <FieldRow label="Charity number" value={row.charity_number || '—'} />
          <FieldRow label="Other funders" value={row.has_other_funders ? `Yes — ${row.other_funders_detail || '(no detail)'} (${row.other_funders_amount || '—'})${row.match_funding ? ', match funded' : ''}` : 'No'} />
          <FieldRow label="Project duration" value={row.project_duration || '—'} />
          <FieldRow label="Total project cost" value={row.total_funding_required} />
          <FieldRow label="Project benefit" value={row.project_benefit} wide />
          <FieldRow label="Agrees to promote us?" value={row.agree_to_promote ? 'Yes' : 'No'} />
          <FieldRow label="Promotion plan" value={row.promotion_detail || '—'} />
          <FieldRow label="Needs branding assets" value={row.need_branding_assets ? 'Yes' : 'No'} />
          <FieldRow label="Volunteering" value={[
            row.volunteer_heritage && 'Heritage',
            row.volunteer_glossfest && 'GlossFest',
            row.volunteer_bonfire && 'Bonfire',
          ].filter(Boolean).join(' · ') || '—'} />

          <div className="lg:col-span-2 mt-2 border-t border-zinc-200 pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-widest text-[#52525B]">Bank details (sensitive)</span>
              <button
                type="button"
                onClick={() => setShowBank((v) => !v)}
                data-testid={`donation-request-row-${row.id}-bank-toggle`}
                className="text-xs text-[#F0A500] hover:text-[#D99500] font-semibold inline-flex items-center gap-1"
              >
                {showBank ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {showBank ? 'Hide' : 'Reveal'}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
              <FieldRow label="Bank name" value={row.bank_name || '—'} />
              <FieldRow label="Account name" value={row.account_name || '—'} />
              <FieldRow label="Account number" value={showBank ? (row.account_number || '—') : maskBankNumber(row.account_number)} />
              <FieldRow label="Sort code" value={showBank ? (row.sort_code || '—') : (row.sort_code ? '••-••-••' : '—')} />
            </div>
          </div>

          {(row.referral_name || row.referral_email || row.referral_phone) && (
            <div className="lg:col-span-2 mt-2 border-t border-zinc-200 pt-4">
              <span className="text-xs font-bold uppercase tracking-widest text-[#52525B] mb-2 block">Member referral</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                <FieldRow label="Name" value={row.referral_name || '—'} />
                <FieldRow label="Age" value={row.referral_age || '—'} />
                <FieldRow label="Email" value={row.referral_email || '—'} />
                <FieldRow label="Mobile" value={row.referral_phone || '—'} />
              </div>
            </div>
          )}

          <div className="lg:col-span-2 flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-zinc-200">
            <span className="text-xs font-bold uppercase tracking-widest text-[#52525B] mr-2">Set status:</span>
            {STATUSES.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => onStatusChange(row.id, s.key)}
                data-testid={`donation-request-row-${row.id}-set-${s.key}`}
                className={`px-3 py-1 text-xs font-bold uppercase tracking-wider transition-colors ${
                  row.status === s.key ? s.cls : 'bg-zinc-100 text-[#52525B] hover:bg-zinc-200'
                }`}
              >
                {s.label}
              </button>
            ))}
            <span className="text-xs text-zinc-400 ml-auto">
              Retain data: {row.retain_data ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldRow({ label, value, wide }) {
  return (
    <div className={wide ? 'lg:col-span-2' : ''}>
      <div className="text-[11px] uppercase tracking-widest font-bold text-[#71717A] mb-1">{label}</div>
      <div className="text-[#0A0A0A] whitespace-pre-wrap break-words">{value || '—'}</div>
    </div>
  );
}

function Dashboard({ token, me, onLogout }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openIds, setOpenIds] = useState(new Set());
  const [donationsOpen, setDonationsOpen] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rs, st] = await Promise.all([
        axios.get(`${API}/admin/donation-requests`, { headers: authHeaders(token) }),
        axios.get(`${API}/admin/donations/settings`, { headers: authHeaders(token) }),
      ]);
      setRows(rs.data);
      setDonationsOpen(Boolean(st.data.donations_open));
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else setError('Failed to load donation requests.');
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const handleToggleDonations = async () => {
    setToggleBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/donations/settings`, { donations_open: !donationsOpen }, { headers: authHeaders(token) });
      setDonationsOpen(Boolean(data.donations_open));
    } catch (err) {
      if (err.response?.status === 403) setError('Only a superadmin can toggle the donations window.');
      else setError('Failed to update settings.');
    } finally {
      setToggleBusy(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/donation-requests/${id}`, { status }, { headers: authHeaders(token) });
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch {
      setError('Failed to update status.');
    }
  };

  const handleDownloadCsv = async () => {
    try {
      const response = await axios.get(`${API}/admin/donation-requests.csv`, {
        headers: authHeaders(token), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'] || '';
      const match = cd.match(/filename=([^;]+)/);
      link.setAttribute('download', match ? match[1].trim() : 'donation-requests.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download CSV.');
    }
  };

  const filtered = rows.filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      r.organisation_name?.toLowerCase().includes(q) ||
      r.contact_name?.toLowerCase().includes(q) ||
      r.contact_email?.toLowerCase().includes(q) ||
      r.organisation_type?.toLowerCase().includes(q)
    );
  });

  const counts = rows.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  return (
    <div className="bg-[#F4F4F5] min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="admin-donations-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-2">Admin Dashboard</span>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A]">Donation Requests</h1>
            <p className="text-sm text-[#52525B] mt-2" data-testid="admin-donation-counts">
              {rows.length} total &middot; {counts.new || 0} new &middot; {counts.approved || 0} approved &middot; {counts.declined || 0} declined &middot; {counts.info_requested || 0} info requested
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button type="button" onClick={load} data-testid="admin-donations-refresh-btn" className="px-4 py-2 bg-zinc-200 hover:bg-zinc-300 text-[#0A0A0A] font-semibold text-sm">Refresh</button>
            <button type="button" onClick={handleDownloadCsv} disabled={rows.length === 0} data-testid="admin-donations-csv-btn" className="px-4 py-2 bg-[#F0A500] hover:bg-[#d99400] disabled:opacity-50 text-[#0A0A0A] font-bold uppercase tracking-wider text-xs">Download CSV</button>
          </div>
        </div>

        {/* Toggle (superadmin only) */}
        {me.role === 'superadmin' && (
          <div className="bg-[#0A0A0A] text-white p-5 sm:p-6 border-l-4 border-[#F0A500] mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="admin-donations-toggle">
            <div>
              <div className="text-xs uppercase tracking-widest font-bold text-[#F0A500] mb-1">Public Donation Page</div>
              <div className="text-sm text-zinc-300">
                {donationsOpen
                  ? <>Currently <strong className="text-white">OPEN</strong> — the page appears in the menu and accepts submissions.</>
                  : <>Currently <strong className="text-white">CLOSED</strong> — the page is hidden from the menu and shows a "currently closed" message.</>
                }
              </div>
            </div>
            <button
              type="button"
              onClick={handleToggleDonations}
              disabled={toggleBusy}
              data-testid="admin-donations-toggle-btn"
              className={`px-5 py-2.5 font-bold uppercase tracking-wider text-xs transition-colors disabled:opacity-60 ${
                donationsOpen ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-[#F0A500] hover:bg-[#d99400] text-[#0A0A0A]'
              }`}
            >
              {toggleBusy ? 'Updating…' : donationsOpen ? 'Close donations' : 'Open donations'}
            </button>
          </div>
        )}

        <div className="bg-white border border-zinc-200 p-4 mb-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <Input
            placeholder="Search by organisation, contact name, email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-donations-search"
            className="flex-1"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="admin-donations-status-filter"
            className="border border-zinc-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#F0A500]"
          >
            <option value="all">All statuses ({rows.length})</option>
            {STATUSES.map((s) => (
              <option key={s.key} value={s.key}>{s.label} ({counts[s.key] || 0})</option>
            ))}
          </select>
        </div>

        {error && <p data-testid="admin-donations-error" className="text-sm text-red-600 mb-4">{error}</p>}

        {loading ? (
          <p className="text-sm text-[#52525B]">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[#52525B] italic" data-testid="admin-donations-empty">
            {rows.length === 0 ? 'No donation requests yet.' : 'No matches for your filters.'}
          </p>
        ) : (
          <div className="space-y-3">
            {filtered.map((row) => (
              <RequestRow
                key={row.id}
                row={row}
                isOpen={openIds.has(row.id)}
                onToggle={() => setOpenIds((s) => {
                  const next = new Set(s);
                  if (next.has(row.id)) next.delete(row.id); else next.add(row.id);
                  return next;
                })}
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminDonationRequestsPage() {
  const { token, me, checking, loginSuccess, logout } = useAdminAuth();
  if (checking) return <div className="min-h-[70vh] flex items-center justify-center text-zinc-500">Checking session…</div>;
  if (!token || !me) {
    return (
      <div data-testid="admin-donations-page">
        <SEO title="Admin Sign-in" description="Restricted admin area." path="/admin/donation-requests" noIndex />
        <AdminLoginGate title="Donations Admin" subtitle="Sign in to manage donation requests." onLoggedIn={loginSuccess} />
      </div>
    );
  }
  return (
    <div data-testid="admin-donations-page">
      <SEO title="Admin — Donation Requests" description="Restricted admin area." path="/admin/donation-requests" noIndex />
      <AdminTabs current="donations" me={me} onLogout={logout} />
      <Dashboard token={token} me={me} onLogout={logout} />
    </div>
  );
}
